import { useId } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Navigate, useParams } from "react-router-dom";
import { t } from "ttag";

import { PasswordTips } from "@/app/components/legacy-components";
import { ROUTES } from "@/app/router";
import { EmbeddablesScreenLayout } from "@/features/embeddables/components";
import { useEmbeddablesUser } from "@/features/embeddables/context";
import { useSetEmbeddablesPassword } from "@/features/embeddables/screens/account/hooks";
import { LogoLoader } from "@/shared/components";
import { confirmPasswordSchema } from "@/shared/models";
import { Form, Layout, PasswordInput, SectionHeader } from "@/shared/ui";

export const Account = () => {
  const formId = useId();

  const { email } = useEmbeddablesUser();

  const { userCode } = useParams();

  const form = useForm({
    resolver: zodResolver(confirmPasswordSchema),
    defaultValues: {
      password: "",
      passwordConfirmation: "",
    },
  });

  const setPasswordMutation = useSetEmbeddablesPassword();

  if (!userCode) {
    return <Navigate replace to={ROUTES.LOGIN} />;
  }

  const handleSubmit = form.handleSubmit((data) => {
    setPasswordMutation.mutate({ ...data, token: userCode });
  });

  return (
    <EmbeddablesScreenLayout>
      <LogoLoader show={setPasswordMutation.isPending} fullscreen />

      <Layout.Content>
        <SectionHeader
          title={t`Create your password`}
          description={t`You are creating an account for the email ${email}`}
        />

        <Form.Provider {...form}>
          <section className="flex w-full grow flex-col gap-1.5">
            <form
              id={formId}
              onSubmit={handleSubmit}
              className="grid content-start gap-x-8 gap-y-1.5 sm:grid-cols-2"
            >
              <Form.Field
                control={form.control}
                name="password"
                render={({ field }) => (
                  <Form.Item>
                    <Form.Label>{t`Password *`}</Form.Label>
                    <Form.Control>
                      <PasswordInput
                        {...field}
                        placeholder={t`Password`}
                        autoComplete="new-password"
                      />
                    </Form.Control>
                    <Form.Message compact={false} />
                  </Form.Item>
                )}
              />

              <Form.Field
                control={form.control}
                name="passwordConfirmation"
                render={({ field }) => (
                  <Form.Item>
                    <Form.Label>{t`Confirm password *`}</Form.Label>
                    <Form.Control>
                      <PasswordInput
                        {...field}
                        placeholder={t`Confirm password`}
                        autoComplete="new-password"
                      />
                    </Form.Control>
                    <Form.Message compact={false} />
                  </Form.Item>
                )}
              />
            </form>

            <PasswordTips />
          </section>
        </Form.Provider>
      </Layout.Content>

      <Layout.Footer>
        <Layout.Action type="submit" form={formId}>
          {t`Submit`}
        </Layout.Action>
      </Layout.Footer>
    </EmbeddablesScreenLayout>
  );
};
